import {create} from 'zustand'
import {ICompany, ISubject, TCompanyId, IUser} from "../types";
import * as companiesService from "../services/companiesService";
import * as subjectsService from "../services/subjectsService";
import {NIL as nilUUID} from "uuid"
import {whoAmI} from "../services/userService";

/**
 * Represents the state structure for the Cross feature capabilities for the
 * Neurotune application.
 *
 * @typedef {Object} NeurotuneState
 * @property {ICompany} [activeCompany] - The currently active company, if any.
 * @property {Array<ICompany>} companies - The list of companies.
 * @property {boolean} companiesLoading - Indicates whether the companies data is being loaded.
 * @property {Array<ISubject>} subjects - The list of subjects.
 * @property {boolean} subjectsLoading - Indicates whether the subjects data is being loaded.
 */
export type NeurotuneState = {
    user: IUser
    activeCompany: ICompany
    companies: Array<ICompany>
    companiesLoading: boolean
    subjects: Array<ISubject>
    subjectsLoading: boolean
    isOnline : boolean
    /** Redirect mode (NDC webview) only: a request 401'd, so the gate must
     * show the LoginPage in place. Set by the apiClient interceptor via
     * reauthRedirect.reportAuthExpired; cleared when bootstrap succeeds. */
    authExpired: boolean
}

/**
 * Defines the set of Cross Feature actions available in the Neurotune system.
 *
 * @typedef {Object} NeurotuneActions
 * @property {Function} loadSubjects Loads a list of subjects for a given company.
 *                                   Returns a promise that resolves to an array of subjects.
 * @param {TCompanyId} companyId The identifier of the company whose subjects are to be loaded.
 * @returns {Promise<Array<ISubject>>} A promise resolving to an array of subject objects.
 *
 * @property {Function} loadCompanies Loads a list of all companies.
 *                                    Returns a promise that resolves to an array of company objects.
 * @returns {Promise<Array<ICompany>>} A promise resolving to an array of company objects.
 *
 * @property {Function} setActiveCompany Sets the currently active company.
 * @param {ICompany} company The company to be set as active.
 */
export type NeurotuneActions = {
    whoAmI: () => Promise<void>
    loadSubjects: (companyId: TCompanyId) => Promise<Array<ISubject>>
    loadCompanies: () => Promise<Array<ICompany>>
    setActiveCompany: (company: ICompany) => void
    setCompanies: (companies: Array<ICompany>) => void
    setOnline: (online : boolean) => void
    setAuthExpired: (expired: boolean) => void
}

/**
 * `useNeurotuneStore` is a Zustand store that manages the state and actions for Neurotune data,
 * including companies and subjects. It provides functions to load data asynchronously and update
 * the state based on the results of the loading operations.
 *
 * The store includes the following state properties:
 * - `companies`: An array of company objects.
 * - `companiesLoading`: A boolean indicating whether the companies are in the process of being loaded.
 * - `subjects`: An array of subject objects associated with the companies.
 * - `subjectsLoading`: A boolean indicating whether the subjects are in the process of being loaded.
 * - `activeCompany`: The currently selected or active company.
 *
 * The store also provides the following actions:
 * - `setActiveCompany(company)`: Sets the provided company as the active company in the store.
 * - `loadCompanies()`: Asynchronously loads the list of companies. On success, updates `companies` and sets `companiesLoading` to `false`. On error, logs the error and ensures `companiesLoading` is set to `false`.
 * - `loadSubjects(companyId)`: Asynchronously loads the list of subjects associated with a specific company identified by `companyId`. On success, updates `subjects` and sets `subjectsLoading` to `false`. On error, logs the error and ensures `subjectsLoading` is set to `false`.
 */
export const useNeurotuneStore = create<NeurotuneState & NeurotuneActions>((set, get) => ({
    // placeholder company so we don't have to type as optional. This will be setup in the loading Page
    user: {
        id: nilUUID,
        email: "Not logged in",
        metadata: {}
    },
    activeCompany: {
        id: nilUUID,
        name: "",
        createdAt: ""
    },
    companies: [],
    companiesLoading: false,
    subjects: [],
    subjectsLoading: false,
    isOnline: false,
    authExpired: false,

    whoAmI: async (): Promise<void> => {
        const user = await whoAmI()
        set((state) => ({...state, user}))
    },
    setOnline: (online : boolean) => {
        set({isOnline:online})
    },
    setAuthExpired: (expired: boolean) => {
        set({authExpired: expired})
    },
    setActiveCompany: async (company) => {
        set((state) => ({state, activeCompany: company, subjects: []}))
    },
    setCompanies: (companies) => {
        set((state) => ({state, companies: companies}))
    },
    loadCompanies: async () => {
        set({companiesLoading: true})
        try {
            const foundCompanies = await companiesService.loadCompanies()
            set({companies: foundCompanies, companiesLoading: false})
            return foundCompanies
        } catch (error) {
            console.log(error)
            set({companiesLoading: false})
            // Re-throw so callers can distinguish a failed fetch from a
            // successful-but-empty result (loading gate retry logic depends
            // on this).
            throw error
        }
    },
    loadSubjects: async (companyId: TCompanyId) => {
        set({subjectsLoading: true})
        try {
            const subjects = await subjectsService.loadSubjects(companyId)
            set({subjects, subjectsLoading: false})
        } catch (error) {
            console.log(error)
            set({subjectsLoading: false})
        }
        return get().subjects
    },
}))